import { ia, iaRa, inRn } from 'lib/helpers/utility';

export const calculateSurcharge = (baseAmount, percentage) => {
  return Math.round(baseAmount * percentage);
};

export const calculateTotalWithSurcharge = (baseAmount, surchargeAmount) => {
  return baseAmount + surchargeAmount;
};

// Mirrors the invoice modal's math (ElectronicInvoicing InvoiceSummary/lib/helpers):
// self-pay and cash lines are charged at `pt_balance ?? total_cost_cents`, and products and
// packages are always patient-pay.
const SELF_PAY_PROCEDURE_TYPES = ['self_pay', 'cash'];

const firstDefined = (item, fields) =>
  fields.map((field) => item?.[field]).find((value) => value !== null && value !== undefined);

const sumItems = (items, fields) =>
  iaRa(items).reduce((sum, item) => sum + inRn(firstDefined(item, fields)), 0);

// Cost share is stored per line item — the same `deductible` / `copay` / `co_insurance`
// fields the expanded invoice details grid renders. Older invoices only carry the
// `last_applied` snapshot, so that is the fallback.
export const invoiceCostShare = (invoice) => {
  const procedures = iaRa(invoice?.procedures);
  const applied = invoice?.last_applied || {};

  const selfPayProcedures = procedures.filter((procedure) =>
    SELF_PAY_PROCEDURE_TYPES.includes(procedure?.type)
  );

  const selfPay =
    sumItems(selfPayProcedures, ['pt_balance', 'total_cost_cents', 'charge']) +
    sumItems(invoice?.products, ['total_amount_cents']) +
    sumItems(invoice?.packages, ['total_amount_cents']);

  const fromProcedures = (field) => sumItems(procedures, [field]);
  const withFallback = (field, snapshot) => fromProcedures(field) || inRn(snapshot?.value);

  return {
    selfPay,
    deductible: withFallback('deductible', applied?.deductible),
    coInsurance: withFallback('co_insurance', applied?.co_insurance),
    copay: withFallback('copay', applied?.co_payment)
  };
};

// An invoice is "insurance backed" when any line is routed to a payer — self-pay, cash,
// products and packages are billed straight to the patient.
const PATIENT_PAY_TYPES = ['self_pay', 'cash', 'product', 'package'];

export const hasInsuranceLines = (invoices) =>
  iaRa(invoices).some((invoice) =>
    iaRa(invoice?.procedures).some(
      (procedure) => !PATIENT_PAY_TYPES.includes(procedure?.type) || ia(procedure?.primary)
    )
  );

// Copy for the summary alert when the entered amount no longer matches the selected items.
export const paymentMismatchAlert = ({ difference, invoiceCount, hasInsurance }) => {
  if (!difference) return null;

  if (difference > 0)
    return {
      color: 'primary',
      message: 'Increasing the payment amount in this section will leave a patient credit'
    };

  const split = invoiceCount > 1 ? ' The amount will be split evenly across invoices' : '';
  const allocation = hasInsurance
    ? `${split ? ' and' : ' The amount will'} be allocated in the following order: Copayment, Deductible, Co-insurance, Self-pay`
    : '';

  return {
    color: 'warning',
    message: `Reducing the payment amount in this section will leave a patient balance.${split}${allocation}${split || allocation ? '.' : ''}`
  };
};
